import React from 'react';
import { motion } from 'framer-motion';

import { Z_INDEX } from '../../utils/constants/z-index.constant';
import brandIcon from '../../assets/images/logos/ba-brand-icon-colored.png';

/**
 * Routes that render their own sticky bottom bar on small screens (the "Contact
 * seller" CTA and friends). The launcher lifts above them so it never covers, or is
 * covered by, the page's primary action.
 */
const STICKY_BAR_ROUTES = [/^\/product\//, /^\/account/, /^\/requests/, /^\/my-listings/];

export const hasStickyBottomBar = (pathname: string): boolean =>
  STICKY_BAR_ROUTES.some((pattern) => pattern.test(pathname));

/**
 * The 3D effect is built from light rather than a bitmap, so it stays crisp at any
 * density and can react to hover and press:
 *   - a warm off-white body, because the brand icon is orange and would disappear
 *     against the brand gradient;
 *   - an inset highlight along the top edge and an inset shade along the bottom,
 *     which is what reads as a convex dome;
 *   - a hairline rim light around the whole edge;
 *   - two drop shadows — a tight contact shadow plus a wider brand-tinted glow.
 */
const SURFACE = 'linear-gradient(160deg, #FFFFFF 0%, #FFF7EF 52%, #F7E4D2 100%)';

const RIM = 'inset 0 0 0 1px rgba(255, 255, 255, 0.75)';
const TOP_HIGHLIGHT = 'inset 0 2px 3px rgba(255, 255, 255, 0.95)';

const RESTING_SHADOW = [
  RIM,
  TOP_HIGHLIGHT,
  'inset 0 -4px 7px rgba(180, 84, 28, 0.26)',
  '0 5px 10px rgba(33, 33, 33, 0.16)',
  '0 14px 28px rgba(229, 83, 37, 0.28)',
].join(', ');

const HOVER_SHADOW = [
  RIM,
  TOP_HIGHLIGHT,
  'inset 0 -5px 9px rgba(180, 84, 28, 0.28)',
  '0 8px 14px rgba(33, 33, 33, 0.18)',
  '0 20px 38px rgba(229, 83, 37, 0.36)',
].join(', ');

/** Pressed: the dome flattens and sits down onto its shadow. */
const PRESSED_SHADOW = [
  RIM,
  'inset 0 3px 6px rgba(180, 84, 28, 0.30)',
  'inset 0 -1px 2px rgba(255, 255, 255, 0.7)',
  '0 2px 5px rgba(33, 33, 33, 0.20)',
  '0 6px 12px rgba(229, 83, 37, 0.22)',
].join(', ');

interface Props {
  isOpen: boolean;
  unreadCount: number;
  pathname: string;
  onClick: () => void;
  className?: string;
}

const AssistantLauncher: React.FC<Props> = ({ isOpen, unreadCount, pathname, onClick, className }) => {
  const lifted = hasStickyBottomBar(pathname);

  return (
    <motion.button
      type="button"
      onClick={onClick}
      aria-label={isOpen ? 'Close the BaoAfrik assistant' : 'Open the BaoAfrik assistant'}
      aria-expanded={isOpen}
      initial={{ scale: 0, opacity: 0, y: 8 }}
      animate={{ scale: 1, opacity: 1, y: 0, boxShadow: RESTING_SHADOW }}
      whileHover={{ y: -3, scale: 1.05, boxShadow: HOVER_SHADOW }}
      whileTap={{ y: 1, scale: 0.95, boxShadow: PRESSED_SHADOW }}
      transition={{ type: 'spring', stiffness: 420, damping: 26 }}
      className={`fixed right-4 flex h-[60px] w-[60px] items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-[#E55325]/60 focus-visible:ring-offset-2 sm:right-6 ${
        // Clears the mobile sticky CTA bars; desktop hides those, so 1.5rem is fine.
        lifted ? 'bottom-24 lg:bottom-6' : 'bottom-6'
      } ${className ?? ''}`}
      style={{
        background: SURFACE,
        boxShadow: RESTING_SHADOW,
        // Above ProductDetail's z-[60] sticky bar, below toasts.
        zIndex: Z_INDEX.TOOLTIP,
        WebkitTapHighlightColor: 'transparent',
      }}
    >
      {/* Specular sheen: a soft light source in the upper-left, sitting above the
          body but below the icon. */}
      <span
        aria-hidden
        className="pointer-events-none absolute inset-0 rounded-full"
        style={{
          background:
            'radial-gradient(120% 90% at 30% 12%, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.35) 38%, rgba(255,255,255,0) 62%)',
        }}
      />

      {isOpen ? (
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden
          className="relative"
          style={{ color: '#E55325' }}
        >
          <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
        </svg>
      ) : (
        <img
          src={brandIcon}
          alt=""
          aria-hidden
          width={34}
          height={34}
          className="relative h-[34px] w-[34px] object-contain"
          style={{
            // Grounds the mark on the surface so it reads as embossed, not pasted on.
            filter: 'drop-shadow(0 1px 1px rgba(146, 64, 14, 0.28))',
          }}
        />
      )}

      {!isOpen && unreadCount > 0 ? (
        <span
          className="absolute -right-0.5 -top-0.5 flex h-5 min-w-[20px] items-center justify-center rounded-full px-1 text-[11px] font-bold text-white"
          style={{
            background: 'linear-gradient(160deg, #F0574A 0%, #D32F2F 100%)',
            boxShadow:
              'inset 0 1px 1px rgba(255,255,255,0.45), 0 2px 4px rgba(33,33,33,0.3)',
          }}
          aria-label={`${unreadCount} new ${unreadCount === 1 ? 'reply' : 'replies'}`}
        >
          {unreadCount > 9 ? '9+' : unreadCount}
        </span>
      ) : null}
    </motion.button>
  );
};

export default AssistantLauncher;
